// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import { ChangeRequestLikelihoodModel } from "@insite/client-framework/Types/ApiModels";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import InfoFill from "@store/Library/Components/Toolkit/Icons/Info/InfoFill";
import likelihoodOfAcceptance from "@store/Framework/Common/Utilities/likelihoodOfAcceptance";
import updateChangeRequestOrder from "@store/Framework/Store/Greif/Pages/RequestOrderChange/Handler/UpdateChangeRequestOrder";
import Status, { StatusStyles } from "@store/Library/Components/Status";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {}

const mapStateToProps = (state: ApplicationState) => ({
    changeRequestOrder: state.greif.pages.orderChange.changeRequestOrder,
    orderState: getOrderState(state, state.pages.orderDetails.orderNumber),
});

const mapDispatchToProps = {
    updateChangeRequestOrder,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderChangeRequestLikelihoodStyles {
    wrapper?: InjectableCss;
    status?: StatusStyles;
    statusTextWrapper?: InjectableCss;
    probabilityText?: TypographyPresentationProps;
    confirmationText?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export const orderChangeRequestLinesListStyles: OrderChangeRequestLikelihoodStyles = {
    status: {
        wrapper: {
            css: css`
                margin-bottom: 0;
                width: auto;
                border-radius: ${pxToRem(25)};
                padding: ${pxToRem(5, 24)};
                height: ${pxToRem(50)};
            `,
        },
        content: {
            css: css`
                display: flex;
                align-items: center;
            `,
        },
        icon: {
            src: AlertTriangle,
            size: 24,
        },
    },
    tooltip: {
        iconProps: {
            src: InfoFill,
            size: 24,
        },
    },
    statusTextWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            margin: 0 ${pxToRem(10)};
        `,
    },
    probabilityText: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    confirmationText: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-style: italic;
        `,
    },
};

const styles = orderChangeRequestLinesListStyles;

const OrderChangeRequestLikelihood: FC<Props> = ({ changeRequestOrder, orderState, updateChangeRequestOrder }) => {
    const order = orderState.value;

    const initialValue: ChangeRequestLikelihoodModel = {
        likelihoodValue: { probability: 9, typeOfChange: undefined, lineId: undefined },
        color: undefined,
        label: undefined,
    };

    useEffect(() => {
        updateChangeRequestOrder({
            changeRequestOrder: {
                ...changeRequestOrder,
                likelihoodOfChange: initialValue,
            },
        });
    }, []);

    useEffect(() => {
        if (order && changeRequestOrder) {
            updateChangeRequestOrder({
                changeRequestOrder: {
                    ...changeRequestOrder,
                    likelihoodOfChange: {
                        likelihoodValue: likelihoodOfAcceptance(changeRequestOrder, order),
                    },
                },
            });
        }
    }, [order, changeRequestOrder.newLines, changeRequestOrder.orderLines, changeRequestOrder.newRequestedDate]);

    const getType = (likelihoodValue: number) => {
        switch (likelihoodValue) {
            case 1:
                return {
                    ...changeRequestOrder?.likelihoodOfChange,
                    color: "red",
                    label: translate("Change unlikely"),
                    description: siteMessage("ChangeRequest_ChangeUnlikely"),
                } as ChangeRequestLikelihoodModel;
            case 2:
                return {
                    ...changeRequestOrder?.likelihoodOfChange,
                    color: "yellow",
                    label: translate("Change conditional"),
                    description: siteMessage("ChangeRequest_ChangeConditional"),
                } as ChangeRequestLikelihoodModel;
            case 3:
                return {
                    ...changeRequestOrder?.likelihoodOfChange,
                    color: "green",
                    label: translate("Change likely"),
                    description: siteMessage("ChangeRequest_ChangeLikely"),
                } as ChangeRequestLikelihoodModel;
            default:
                return {
                    ...changeRequestOrder?.likelihoodOfChange,
                } as ChangeRequestLikelihoodModel;
        }
    };

    const computedLikelihood =
        getType(changeRequestOrder?.likelihoodOfChange?.likelihoodValue.probability || 9) || undefined;

    if (computedLikelihood?.likelihoodValue?.probability === 9) {
        return null;
    }

    return (
        <Status showIcon type={computedLikelihood.color} extendedStyles={styles.status}>
            <StyledWrapper {...styles.statusTextWrapper}>
                <Typography {...styles.probabilityText}>{computedLikelihood?.label}</Typography>
                {computedLikelihood?.label && (
                    <Typography {...styles.confirmationText}>{translate("(Pending confirmation)")}</Typography>
                )}
            </StyledWrapper>
            {computedLikelihood.description && <Tooltip {...styles.tooltip} text={computedLikelihood.description} />}
        </Status>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderChangeRequestLikelihood);
